import { useRef, type PointerEvent, type RefObject } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';

interface SwipeProps {
  onPointerDown: (event: PointerEvent) => void;
  onPointerMove: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
}

interface Result {
  sheetRef: RefObject<HTMLDivElement | null>;
  swipeProps: SwipeProps | Record<string, never>;
}

const CLOSE_THRESHOLD_PX = 100;
const RESET_TRANSITION_MS = 200;

export const useSwipeToClose = (onClose: () => void): Result => {
  const isMobile = useIsMobile();
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const dragStartYRef = useRef<number | null>(null);
  const dragDeltaRef = useRef(0);

  const handlePointerDown = (event: PointerEvent) => {
    dragStartYRef.current = event.clientY;
    dragDeltaRef.current = 0;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (dragStartYRef.current === null || !sheetRef.current) {
      return;
    }

    const delta = event.clientY - dragStartYRef.current;

    if (delta <= 0) {
      return;
    }

    dragDeltaRef.current = delta;
    sheetRef.current.style.transform = `translateY(${delta}px)`;
    sheetRef.current.style.transition = 'none';
  };

  const handlePointerUp = (event: PointerEvent) => {
    if (!sheetRef.current) {
      return;
    }

    event.currentTarget.releasePointerCapture(event.pointerId);

    const shouldClose = dragDeltaRef.current > CLOSE_THRESHOLD_PX;

    if (shouldClose) {
      sheetRef.current.style.animation = 'none';
      sheetRef.current.style.transition = `transform ${RESET_TRANSITION_MS}ms ease-out`;
      sheetRef.current.style.transform = 'translateY(100%)';

      window.setTimeout(onClose, RESET_TRANSITION_MS);
    } else {
      sheetRef.current.style.transition = `transform ${RESET_TRANSITION_MS}ms ease-out`;
      sheetRef.current.style.transform = '';
    }

    dragStartYRef.current = null;
    dragDeltaRef.current = 0;
  };

  const swipeProps: SwipeProps | Record<string, never> = isMobile
    ? {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
      }
    : {};

  return { sheetRef, swipeProps };
};
